import { useState, useCallback } from "react";
import { useApp } from "../context/AppContext";
import { useSalon } from "../context/SalonContext";
import { Employee } from "../types/salon";
import { notifyError, notifySuccess } from "../utils/notify";
import { IconCheck, IconAlertCircle } from "@tabler/icons-react";

/**
 * Hook for syncing RollFi payroll users with Pagoda staff members
 * Matches users by phone number and updates staff records with RollFi user IDs
 */
export function usePayrollUserSync() {
  const { getRollfiActiveUsers, updateStaff } = useApp();
  const salonContext = useSalon() as any;
  const { businessInfo, employees, setEmployees } = salonContext;
  const [isSyncing, setIsSyncing] = useState(false);

  const normalizePhoneNumber = (phone: string): string => {
    // Remove all non-digit characters
    return phone.replace(/\D/g, "");
  };

  const syncPayrollUsers = useCallback(
    async (options?: {
      silent?: boolean;
    }): Promise<{ success: boolean; updatedCount: number }> => {
      if (!businessInfo?.rollfi__company_id) {
        if (!options?.silent) {
          notifyError("No RollFi company ID found", {
            title: "Error",
            icon: <IconAlertCircle />,
          });
        }
        return { success: false, updatedCount: 0 };
      }

      try {
        setIsSyncing(true);

        const response = await getRollfiActiveUsers({
          companyId: businessInfo.rollfi__company_id,
          workerType: "W2",
        });

        // Match RollFi users to Pagoda staff by phone number
        const updates: Array<{
          staffId: string;
          rollfiUserId: string;
          name: string;
        }> = [];

        response.user.forEach((rollfiUser) => {
          const rollfiPhone = normalizePhoneNumber(rollfiUser.phoneNumber);

          // Match against ALL employees (both active and inactive)
          const matchedEmployee = employees?.find((emp: Employee) => {
            const pagodaPhone = normalizePhoneNumber(
              `${emp.area_code || ""}${emp.phone_number || ""}`,
            );
            return pagodaPhone === rollfiPhone;
          });

          if (matchedEmployee) {
            updates.push({
              staffId: matchedEmployee.id,
              rollfiUserId: rollfiUser.userId,
              name: `${matchedEmployee.first_name} ${matchedEmployee.last_name}`,
            });
          }
        });

        // Update staff members with rollfi user IDs (only if changed)
        let actualUpdatesCount = 0;
        for (const update of updates) {
          const employeeToUpdate = employees?.find(
            (emp: Employee) => emp.id === update.staffId,
          );
          if (
            employeeToUpdate &&
            employeeToUpdate.rollfi__user_id !== update.rollfiUserId
          ) {
            await updateStaff({
              ...employeeToUpdate,
              rollfi__user_id: update.rollfiUserId,
            });
            actualUpdatesCount++;
          }
        }

        // Update local state
        if (employees) {
          const updatedEmployees = employees.map((emp: Employee) => {
            const update = updates.find((u) => u.staffId === emp.id);
            return update
              ? { ...emp, rollfi__user_id: update.rollfiUserId }
              : emp;
          });
          setEmployees(updatedEmployees);
        }

        if (!options?.silent && actualUpdatesCount > 0) {
          notifySuccess(
            `Successfully synced ${actualUpdatesCount} staff member(s) with RollFi`,
            { title: "Success", icon: <IconCheck /> },
          );
        }

        return { success: true, updatedCount: actualUpdatesCount };
      } catch (error) {
        console.error("Error syncing payroll users:", error);
        if (!options?.silent) {
          notifyError(
            error instanceof Error
              ? error.message
              : "Failed to sync payroll users",
            { title: "Error", icon: <IconAlertCircle /> },
          );
        }
        return { success: false, updatedCount: 0 };
      } finally {
        setIsSyncing(false);
      }
    },
    [businessInfo, employees, getRollfiActiveUsers, updateStaff, setEmployees],
  );

  return {
    syncPayrollUsers,
    isSyncing,
  };
}
